import React from 'react';
import {twMerge} from 'tailwind-merge';
import {cva} from 'class-variance-authority';
import clsx from 'clsx';
import {buttonSizes} from 'utils/uiSizes';

const disabledClasses =
	'disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed disabled:text-gray-400 dark:disabled:text-gray-500 disabled:opacity-70';

const disabledGhostClasses =
	'disabled:bg-transparent disabled:text-black/40 dark:disabled:text-gray-500 disabled:hover:bg-transparent';

const disabledOutlineClasses =
	'disabled:bg-transparent disabled:text-gray-400 dark:disabled:text-gray-600 disabled:border-gray-300 dark:disabled:border-gray-700 disabled:cursor-not-allowed disabled:hover:bg-transparent';

export const buttonVariants = cva(
	clsx(
		'flex items-center justify-center rounded-md',
		'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-opacity-20',
		'transition-colors active:transition-none',
	),
	{
		variants: {
			variant: {
				default: `bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 active:bg-gray-300 dark:active:bg-gray-600 ${disabledClasses}`,
				white: `bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 dark:active:bg-gray-600 ${disabledClasses}`,
				indigo: `bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 ${disabledClasses}`,
				blue: `bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 ${disabledClasses}`,
				green: `bg-green-600 text-white hover:bg-green-700 active:bg-green-800 ${disabledClasses}`,
				red: `bg-red-600 text-white hover:bg-red-700 active:bg-red-800 ${disabledClasses}`,
				yellow: `bg-yellow-600 text-white hover:bg-yellow-700 active:bg-yellow-800 ${disabledClasses}`,
				purple: `bg-purple-600 text-white hover:bg-purple-700 active:bg-purple-800 ${disabledClasses}`,
				dark: `bg-gray-600 dark:bg-gray-700 text-white hover:bg-gray-700 dark:hover:bg-gray-600 active:bg-gray-800 dark:active:bg-gray-500 ${disabledClasses}`,
				outline: `bg-transparent border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-500 active:bg-gray-100 dark:active:bg-gray-700 ${disabledOutlineClasses}`,
				ghost: `bg-transparent text-black/60 dark:text-gray-300 hover:bg-black/5 dark:hover:bg-white/10 active:bg-black/10 dark:active:bg-white/15 ${disabledGhostClasses}`,
			},
			size: {
				xs: buttonSizes.xs,
				sm: buttonSizes.sm,
				default: buttonSizes.default,
				md: buttonSizes.md,
				lg: buttonSizes.lg,
			},
			effect: {
				lifted: 'shadow-lift active:shadow-lift-sm',
				default: 'shadow-none',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
			effect: 'default',
		},
	},
);

export const Button = React.forwardRef(
	({children, className, variant, size, effect, type = 'button', ...props}, ref) => (
		<button
			type={type}
			className={twMerge(buttonVariants({variant, size, effect, className}))}
			ref={ref}
			{...props}
		>
			{children}
		</button>
	),
);

Button.displayName = 'Button';

export default Button;
